import { useState, useCallback, useRef, useEffect } from 'react';
import statusCodes from '../utils/constants/status.codes';
import logger from '../utils/logger';

function useTableFiltering({
    initialFilters = {},
    filterableColumns = [],
    updateQueryParams,
    getQueryParams,
    onFiltersChange,
    columnTypes = {},
}) {
    const [activeFilters, setActiveFilters] = useState({});
    const [filtersRefreshTrigger, setFiltersRefreshTrigger] = useState(0);
    const filtersRef = useRef(initialFilters);
    const filterValuesForPanelRef = useRef([]);
    const columnValuesCache = useRef({});    // Initialize filters from URL parameters
    useEffect(() => {
        const params = getQueryParams();
        const urlFilters = {};
        const newActiveFilters = {};

        Object.keys(params).forEach(key => {
            if (key.startsWith('filter_')) {
                const filterKey = key.replace('filter_', '');
                const filterValues = params[key] ? params[key].split(',').filter(val => val.trim() !== '') : [];
                if (filterValues.length > 0) {
                    // Special handling for statusCode - convert to numbers
                    if (filterKey === 'statusCode') {
                        urlFilters[filterKey] = filterValues.map(val => Number(val.trim())).filter(val => !isNaN(val));
                    } 
                    // Special handling for boolean columns (like exported)
                    else if (filterValues.every(val => val.trim() === 'true' || val.trim() === 'false')) {
                        urlFilters[filterKey] = filterValues.map(val => val.trim() === 'true');
                    }
                    // Special handling for number columns with operators
                    else if (columnTypes[filterKey] === 'number') {
                        const paramValue = params[key];
                        if (paramValue.includes(':')) {
                            // New format: operator:value1,value2
                            const [operator, valuesStr] = paramValue.split(':');
                            const values = valuesStr.split(',').map(val => parseFloat(val.trim())).filter(val => !isNaN(val));
                            urlFilters[filterKey] = { operator, values };
                        } else {
                            // Legacy format: value1,value2 - assume equals
                            const values = filterValues.map(val => parseFloat(val.trim())).filter(val => !isNaN(val));
                            urlFilters[filterKey] = { operator: 'equals', values };
                        }
                    }
                    // Special handling for date columns - convert back to Date objects
                    else if ((filterKey === 'postingDate' || filterKey === 'createdAt') && filterValues.length === 2) {
                        try {
                            // Parse ISO date strings (YYYY-MM-DD format)
                            const startDate = new Date(filterValues[0] + 'T00:00:00');
                            const endDate = new Date(filterValues[1] + 'T23:59:59');
                            
                            // Only set if both dates are valid
                            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                                urlFilters[filterKey] = [startDate, endDate];
                            }
                        } catch (error) {
                            logger.warn(`Failed to parse date filter for ${filterKey}:`, error);
                        }
                    } 
                    else {
                        // For string columns, try to preserve the original data type from the raw data
                        // This handles cases where ECE numbers might be stored as numbers but come from URL as strings
                        urlFilters[filterKey] = filterValues.map(val => {
                            const trimmedVal = val.trim();
                            // If it looks like a number and parses cleanly, try both string and number
                            const numVal = parseFloat(trimmedVal);
                            if (!isNaN(numVal) && String(numVal) === trimmedVal) {
                                return numVal; // Return as number if it's a clean numeric conversion
                            }
                            return trimmedVal; // Return as string otherwise
                        });
                    }
                    newActiveFilters[filterKey] = true;
                }
            }
        });
        // Update filters if URL has filter parameters
        if (Object.keys(urlFilters).length > 0) {
            filtersRef.current = { ...initialFilters, ...urlFilters };
            setActiveFilters(prev => ({ ...prev, ...newActiveFilters }));
            setFiltersRefreshTrigger(prev => prev + 1);
        }
    }, [getQueryParams]);

    // Update column values cache when data changes
    const updateColumnValuesCache = useCallback((data) => {
        if (!data || data.length === 0) {
            return;
        }

        const cache = {};
        filterableColumns.forEach((column) => {
            const mappedValues = data.map((item) => item[column]);
            const filteredValues = mappedValues.filter((value) => value !== null && value !== undefined);
            const uniqueValues = [...new Set(filteredValues)];
            
            // Special handling for statusCode column
            if (column === 'statusCode') {
                uniqueValues.sort((a, b) => Number(a) - Number(b));
                cache[column] = uniqueValues.map((value) => ({
                    label: statusCodes[value] || String(value),
                    value: Number(value) // Keep as number for filtering
                }));
            } 
            // Special handling for boolean columns (like exported)
            else if (uniqueValues.length > 0 && uniqueValues.every(val => typeof val === 'boolean')) {
                // Sort boolean values: false first, then true
                uniqueValues.sort((a, b) => a === b ? 0 : a ? 1 : -1);
                cache[column] = uniqueValues.map((value) => ({
                    label: value ? 'True' : 'False',
                    value: value // Keep as boolean for filtering
                }));
            } 
            else {
                uniqueValues.sort((a, b) => {
                    if (typeof a === "string" && typeof b === "string") return a.localeCompare(b);
                    return 0;
                });
                cache[column] = uniqueValues.map((value) => ({ label: String(value), value }));
            }
        });
        columnValuesCache.current = cache;
    }, [filterableColumns]);

    const applyFilters = useCallback(() => {

        const filterParamsForUrl = {};

        // Get all current URL filter parameters to ensure we clear them if not in use
        const currentParams = getQueryParams();
        const currentFilterKeys = Object.keys(currentParams).filter(key => key.startsWith('filter_'));

        // Clear all existing filter parameters first
        currentFilterKeys.forEach(filterKey => {
            filterParamsForUrl[filterKey] = '';
        });

        // Add all current filter parameters
        Object.entries(filtersRef.current).forEach(([key, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0) {
                // Special handling for date columns
                if ((key === 'postingDate' || key === 'createdAt') && Array.isArray(selectedValues) && selectedValues.length === 2) {
                    // Convert dates to ISO string format for URL
                    const startDate = selectedValues[0] instanceof Date ? selectedValues[0] : new Date(selectedValues[0]);
                    const endDate = selectedValues[1] instanceof Date ? selectedValues[1] : new Date(selectedValues[1]);
                    
                    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                        const startISO = startDate.toISOString().split('T')[0]; // YYYY-MM-DD format
                        const endISO = endDate.toISOString().split('T')[0];
                        filterParamsForUrl[`filter_${key}`] = `${startISO},${endISO}`;
                    } else {
                        filterParamsForUrl[`filter_${key}`] = '';
                    }
                } 
                // Special handling for boolean values
                else if (selectedValues.every(val => typeof val === 'boolean')) {
                    const stringValues = selectedValues.map(val => String(val));
                    filterParamsForUrl[`filter_${key}`] = stringValues.join(',');
                }
                else {
                    // Convert values to strings for URL parameters
                    const stringValues = selectedValues.map(val => String(val));
                    filterParamsForUrl[`filter_${key}`] = stringValues.join(',');
                }
            } else {
                // Explicitly set empty filter parameters to empty string so they get removed from URL
                filterParamsForUrl[`filter_${key}`] = '';
            }
        });

        // Reset pagination to page 1 when filters are applied
        filterParamsForUrl.page = '1';

        updateQueryParams(filterParamsForUrl, { replace: true });

        const newActiveStates = {};

        Object.entries(filtersRef.current).forEach(([key, selectedValues]) => {
            newActiveStates[key] = selectedValues && selectedValues.length > 0;
        });        
        
        // Only update activeFilters if they actually changed to prevent unnecessary re-renders
        setActiveFilters(prevActiveFilters => {
            const hasChanges = Object.keys(newActiveStates).some(key =>
                prevActiveFilters[key] !== newActiveStates[key]
            ) || Object.keys(prevActiveFilters).some(key =>
                prevActiveFilters[key] !== newActiveStates[key]
            );

            // Only trigger re-render when filters actually change
            if (hasChanges) {
                setFiltersRefreshTrigger(prev => {
                    return prev + 1;
                });
                
                // Reset pagination to page 1 when filters change
                if (onFiltersChange) {
                    onFiltersChange();
                }
            }

            return hasChanges ? newActiveStates : prevActiveFilters;
        });
    }, [updateQueryParams, getQueryParams]);
      const applyDataFilters = useCallback((data) => {

        if (!data || data.length === 0) {
            return data;
        }

        let result = [...data];

        // Apply filters
        Object.entries(filtersRef.current).forEach(([key, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0) {
                result = result.filter((item) => {
                    const itemValue = item[key];
                    
                    // Special handling for statusCode column
                    if (key === 'statusCode') {
                        return itemValue !== null && itemValue !== undefined &&
                            selectedValues.includes(Number(itemValue));
                    } 
                    // Special handling for boolean columns (like exported)
                    else if (selectedValues.length > 0 && selectedValues.every(val => typeof val === 'boolean')) {
                        return itemValue !== null && itemValue !== undefined &&
                            selectedValues.includes(Boolean(itemValue));
                    }
                    // Special handling for date columns
                    else if ((key === 'postingDate' || key === 'createdAt') && Array.isArray(selectedValues) && selectedValues.length === 2) {
                        if (itemValue === null || itemValue === undefined) return false;
                        
                        const startDate = selectedValues[0];
                        const endDate = selectedValues[1];
                        
                        // Ensure we have valid Date objects
                        if (!(startDate instanceof Date) || !(endDate instanceof Date)) return true;
                        
                        let itemDate;
                        
                        if (key === 'postingDate') {
                            // postingDate is a string like "2025-06-27T00:00:00"
                            itemDate = new Date(itemValue);
                        } else if (key === 'createdAt') {
                            // createdAt is an epoch integer (Unix timestamp)
                            itemDate = new Date(itemValue);
                        }
                        
                        // Check if itemDate is valid and falls within the range
                        if (isNaN(itemDate.getTime())) return false;
                        
                        // Set time to start/end of day for proper comparison
                        const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
                        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
                        
                        return itemDateOnly >= startDateOnly && itemDateOnly <= endDateOnly;
                    } 
                    else {
                        // Default comparison - handle both string and number cases
                        if (itemValue === null || itemValue === undefined) return false;
                        
                        // Convert both itemValue and selectedValues to strings for comparison
                        const itemValueStr = String(itemValue);
                        const selectedValuesStr = selectedValues.map(val => String(val));
                        
                        // Also try numeric comparison if the value looks like a number
                        const itemValueNum = parseFloat(itemValue);
                        const hasNumericMatch = !isNaN(itemValueNum) && 
                            selectedValues.some(val => {
                                const valNum = parseFloat(val);
                                return !isNaN(valNum) && valNum === itemValueNum;
                            });
                        
                        return selectedValuesStr.includes(itemValueStr) || hasNumericMatch;
                    }
                });
            }
        });

        return result;
    }, []);

    const getColumnValues = useCallback((columnKey) => {
        return columnValuesCache.current[columnKey] || [];
    }, []);

    const setFilterValues = useCallback((values) => {
        filterValuesForPanelRef.current = values;
    }, []);

    const getFilterValues = useCallback(() => {
        return filterValuesForPanelRef.current;
    }, []); return {
        activeFilters,
        filtersRef,
        filtersRefreshTrigger,
        applyFilters,
        applyDataFilters,
        getColumnValues,
        setFilterValues,
        getFilterValues,
        updateColumnValuesCache
    };
}

export default useTableFiltering;
